import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActionButton } from '@shared/abstraction/grid/action-button';
import { ActionMenuComponent } from '@shared/components/action-menu/action-menu.component';
import { TaxSummaryService } from '../../services/tax-summary.service';
import { SidebarItemListService } from '@shared/services/sidebar-item.service';
import { DataMode } from '@shared/modules/rich-editor/helpers/enums/data-mode.enum';
import { delay, filter, switchMap, tap } from 'rxjs';
import { TaxSummaryDetail } from '../../models/tax-summary-detail';

@Component({
  standalone: false,
  selector: 'gstar-tax-rate-summaries-box',
  templateUrl: './tax-rate-summaries-box.component.html',
  styleUrl: './tax-rate-summaries-box.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxRateSummariesBoxComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly translation = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly activatedRouter = inject(ActivatedRoute);
  private readonly taxSummaryService = inject(TaxSummaryService);
  private readonly sidebarItemService = inject(SidebarItemListService);

  taxSummaryDetails: WritableSignal<TaxSummaryDetail[]> = signal([]);
  notFoundMessage: WritableSignal<string> = signal('');
  isForceEditorToRenderAgain = signal(false);

  editorReadOnlyMode = DataMode.readonly;
  actionMenu: ActionButton<TaxSummaryDetail>[] = [
    {
      label: this.translation.instant('shared.edit'),
      command: ({ countryId, taxSummaryId }: TaxSummaryDetail): void => {
        this.router.navigate([
          `/country/${countryId}/tax-rates-summaries/edit/${taxSummaryId}`,
        ]);
      },
      icon: '',
    },
  ];
  menu: Signal<ActionMenuComponent<TaxSummaryDetail> | undefined> = viewChild(
    ActionMenuComponent<TaxSummaryDetail>
  );

  constructor() {
    // Load tax summaries when active tax summary changes
    this.taxSummaryService.activeTaxSummary$
      .pipe(
        switchMap(({ taxSummaryHeader, countryId }) => {
          return this.taxSummaryService.getTaxSummaryByHeaderAndCountry(
            taxSummaryHeader.taxSummaryHeaderId,
            countryId
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((details: TaxSummaryDetail[]) => {
        this.taxSummaryDetails.set(details);
      });

    // Update "no data found" message when country changes
    this.sidebarItemService.selectedCountry$.subscribe(country => {
      if (country) {
        this.notFoundMessage.set(
          this.translation.instant('countryInformation.noInfoFound', {
            country: country.name,
          })
        );
      }
    });

    // Close menu when navigating between different tax summaries
    this.activatedRouter.params
      .pipe(
        tap(() => {
          this.isForceEditorToRenderAgain.set(true);
        }),
        delay(600),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.menu()?.isShow.set(false);
        this.isForceEditorToRenderAgain.set(false);
      });

    // Reload data when country changes in sidebar
    this.sidebarItemService.hasCountryChanged$
      .pipe(
        filter(hasChanged => hasChanged === true),
        tap(() => {
          this.isForceEditorToRenderAgain.set(true);
        }),
        delay(600),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isForceEditorToRenderAgain.set(false);
        this.sidebarItemService.hasCountryChanged.set(false);
      });
  }
}
